import React, { useRef, useCallback, useState } from "react";
import { TextField, InputAdornment, IconButton, Box, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { InsertEmoticon, Mic, Stop, Send, AutoAwesome } from "@mui/icons-material";
import ExpandIcon from "@mui/icons-material/OpenInFull";
import ChannelSelector, { INTERNAL_NOTE_CHANNEL_ID } from "./ChannelSelector";
import { useTranslation } from "react-i18next";
import ComposeAttachMenu from "./ComposeAttachMenu";
import { composerHasSendPayload } from "./composeAttachmentHelpers";
import { composerHasTextPayload, stripHtmlToPlain, isProbablyHtml } from "./composerHtmlUtils";
import {
  COMPOSER_MOBILE_ACTION_BUTTON_SIZE_PX,
  COMPOSER_MOBILE_ROW_HEIGHT_PX,
  COMPOSER_ROW_HEIGHT_PX,
  composerMobileInputRowSx,
  composerPrimaryActionButtonSx,
} from "./composerConstants";
import { COMPOSER_MOBILE_TEXT_FIELD_SX, COMPOSER_TEXT_FIELD_SX } from "./composerTextFieldStyles";
import ComposerMobileToolbar from "./ComposerMobileToolbar";
import RecordingIndicator from "./RecordingIndicator";
import AudioPreview from "./AudioPreview";
import InternalNoteExpandedEditor from "./InternalNoteExpandedEditor";
import { useComposeSelectionFormat } from "../../hooks/useComposeSelectionFormat";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";

const MessageInputField = ({
  message,
  setMessage,
  channelType,
  selectedChannel,
  setSelectedChannel,
  isPersonalWhatsappThread = false,
  formatOptions,
  isRecording,
  recordingDuration = 0,
  startRecording,
  stopRecording,
  discardRecording = () => {},
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  file,
  files = [],
  audioURL,
  onRemoveAudio,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
  sendBlockedMissingPhone = false,
  onOpenProductPicker,
  clientModeOnly = false,
  onRemoveFile,
  clientConversation = null,
  conversationMessages = null,
  onStartVideo,
  videoSending = false,
  videoSendDisabled = false,
  videoSendDisabledHelp = null,
  videoMenuLabel = null,
  onJoinVideo,
  joinVideoSending = false,
  showJoinVideo = false,
  onStartGoogleMeet,
  googleMeetSending = false,
  googleMeetDisabled = false,
  googleMeetDisabledHelp = null,
}) => {
  const inputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { t } = useTranslation("chat");
  const [isInternalEditorOpen, setIsInternalEditorOpen] = useState(false);
  const inputBorderRadius = 16;
  const composerRowHeight = isMobile ? COMPOSER_MOBILE_ROW_HEIGHT_PX : COMPOSER_ROW_HEIGHT_PX;
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const isInternalNote =
    String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID) || channelType === "Xpro";
  const canSend = isInternalNote
    ? composerHasTextPayload(message) || composerHasSendPayload("", file, files, audioURL)
    : composerHasSendPayload(message, file, files, audioURL);
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType,
    channelName,
    isInternalNote,
    isPersonalWhatsapp: isPersonalWhatsappThread,
  });
  const canUseAiSuggestion = !!onGenerateSuggestion && channelType !== "Xpro";
  const aiDisabled = isGeneratingSuggestion || !hasHistory;

  const { composeInputHandlers, selectionToolbar } = useComposeSelectionFormat({
    inputRef,
    setMessage,
    channelType,
    formatOptions,
  });

  const handleOpenProductPickerFromMenu = useCallback(() => {
    onOpenProductPicker?.();
    queueMicrotask(() => inputRef.current?.focus());
  }, [onOpenProductPicker]);

  const compactMessageValue = isInternalNote
    ? isProbablyHtml(message)
      ? stripHtmlToPlain(message, { trim: false })
      : message
    : message;

  const handleCompactMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const handleInternalEditorSend = () => {
    if (sendBlockedMissingPhone) return;
    if (!composerHasTextPayload(message) && !composerHasSendPayload("", file, files, audioURL)) return;
    handleSendMessage();
    setIsInternalEditorOpen(false);
  };

  const textField = (
    <TextField
      fullWidth
      multiline
      minRows={1}
      maxRows={4}
      variant="outlined"
      placeholder={
        selectedChannel === INTERNAL_NOTE_CHANNEL_ID || channelType === "Xpro"
          ? t("input.placeholderInternal")
          : isPersonalWhatsappThread
          ? t("input.placeholderPersonalWhatsapp")
          : channelType
          ? t("input.placeholderChannel", { channel: channelType })
          : t("input.placeholderGeneric")
      }
      value={compactMessageValue}
      onChange={handleCompactMessageChange}
      inputProps={
        isInternalNote
          ? {}
          : { ...composeInputHandlers, "data-xpro-client-compose": "true" }
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if (sendBlockedMissingPhone) return;
          handleSendMessage();
        }
      }}
      InputProps={{
        style: {
          borderRadius: 0,
          fontSize: isMobile ? "16px" : "1rem",
        },
        inputRef: inputRef,
        startAdornment: isMobile ? undefined : (
          <InputAdornment position="start" sx={{ ml: 0.5 }}>
            <IconButton
              onClick={handleEmojiPickerOpen}
              edge="start"
              size="medium"
              aria-label={t("input.emojiAria")}
            >
              <InsertEmoticon fontSize="medium" />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: isMobile ? undefined : (
          <InputAdornment
            position="end"
            sx={{ mr: 0.25, flexWrap: "nowrap", display: "flex", alignItems: "center", gap: 0 }}
          >
            {canUseAiSuggestion ? (
              <Tooltip
                title={
                  aiDisabled
                    ? isGeneratingSuggestion
                      ? t("input.aiGenerating")
                      : t("input.aiNoHistory")
                    : t("input.aiSuggest")
                }
              >
                <span>
                  <IconButton
                    onClick={onGenerateSuggestion}
                    disabled={aiDisabled}
                    size="medium"
                    aria-label={t("input.aiSuggest")}
                  >
                    <AutoAwesome fontSize="medium" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <ComposeAttachMenu
              openFilePicker={openFilePicker}
              showAttachProducts={typeof onOpenProductPicker === "function"}
              onOpenProductPicker={handleOpenProductPickerFromMenu}
              showLocation={false}
              showStartVideo={typeof onStartVideo === "function"}
              onStartVideo={onStartVideo}
              videoSending={videoSending}
              videoSendDisabled={videoSendDisabled}
              videoSendDisabledHelp={videoSendDisabledHelp}
              videoMenuLabel={videoMenuLabel}
              showJoinVideo={showJoinVideo && typeof onJoinVideo === "function"}
              onJoinVideo={onJoinVideo}
              joinVideoSending={joinVideoSending}
              showGoogleMeet={typeof onStartGoogleMeet === "function"}
              onStartGoogleMeet={onStartGoogleMeet}
              googleMeetSending={googleMeetSending}
              googleMeetDisabled={googleMeetDisabled}
              googleMeetDisabledHelp={googleMeetDisabledHelp}
              size="medium"
            />
            {isInternalNote && (
              <Tooltip title={t("compose.expandInternalEditor", "Ampliar editor")}>
                <IconButton
                  onClick={() => setIsInternalEditorOpen(true)}
                  size="medium"
                  aria-label={t("compose.expandInternalEditor", "Ampliar editor")}
                >
                  <ExpandIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
            )}
          </InputAdornment>
        ),
      }}
      sx={isMobile ? COMPOSER_MOBILE_TEXT_FIELD_SX : COMPOSER_TEXT_FIELD_SX}
    />
  );

  const textOrVoiceSlot = isRecording ? (
    <RecordingIndicator
      embedded
      durationSeconds={recordingDuration}
      onStop={stopRecording}
      onDiscard={discardRecording}
    />
  ) : audioURL ? (
    <AudioPreview embedded audioURL={audioURL} onRemove={onRemoveAudio} />
  ) : (
    textField
  );

  const integratedComposer = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "stretch",
        borderRadius: `${inputBorderRadius}px`,
        minHeight: composerRowHeight,
        overflow: "hidden",
        ...composerSurface.shell,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          alignSelf: "stretch",
          px: 0.25,
          ...composerSurface.divider,
        }}
      >
        <ChannelSelector
          integrated
          selectedChannel={selectedChannel}
          setSelectedChannel={setSelectedChannel}
          clientModeOnly={clientModeOnly}
          clientConversation={clientConversation}
          conversationMessages={conversationMessages}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: `${composerRowHeight}px`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {textOrVoiceSlot}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? 0 : 1,
      }}
    >
      {isMobile ? (
        <>
          <Box sx={composerMobileInputRowSx}>
            {integratedComposer}
            {!isRecording && !audioURL ? (
            <ComposerMobileToolbar>
              <IconButton
                onClick={handleEmojiPickerOpen}
                size="small"
                aria-label={t("input.emojiAria")}
              >
                <InsertEmoticon fontSize="small" />
              </IconButton>
              {canUseAiSuggestion ? (
                <Tooltip
                  title={
                    aiDisabled
                      ? isGeneratingSuggestion
                        ? t("input.aiGenerating")
                        : t("input.aiNoHistory")
                      : t("input.aiSuggest")
                  }
                >
                  <span>
                    <IconButton
                      onClick={onGenerateSuggestion}
                      disabled={aiDisabled}
                      size="small"
                      aria-label={t("input.aiSuggest")}
                    >
                      <AutoAwesome fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              <ComposeAttachMenu
                openFilePicker={openFilePicker}
                showAttachProducts={typeof onOpenProductPicker === "function"}
                onOpenProductPicker={handleOpenProductPickerFromMenu}
                showLocation={false}
                showStartVideo={typeof onStartVideo === "function"}
                onStartVideo={onStartVideo}
                videoSending={videoSending}
                videoSendDisabled={videoSendDisabled}
                videoSendDisabledHelp={videoSendDisabledHelp}
                videoMenuLabel={videoMenuLabel}
                showJoinVideo={showJoinVideo && typeof onJoinVideo === "function"}
                onJoinVideo={onJoinVideo}
                joinVideoSending={joinVideoSending}
                showGoogleMeet={typeof onStartGoogleMeet === "function"}
                onStartGoogleMeet={onStartGoogleMeet}
                googleMeetSending={googleMeetSending}
                googleMeetDisabled={googleMeetDisabled}
                googleMeetDisabledHelp={googleMeetDisabledHelp}
                size="small"
              />
              {isInternalNote && (
                <Tooltip title={t("compose.expandInternalEditor", "Ampliar editor")}>
                  <IconButton
                    onClick={() => setIsInternalEditorOpen(true)}
                    size="small"
                    aria-label={t("compose.expandInternalEditor", "Ampliar editor")}
                  >
                    <ExpandIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </ComposerMobileToolbar>
            ) : null}
            <IconButton
              onClick={() => {
                if (sendBlockedMissingPhone) return;
                if (isRecording) {
                  stopRecording();
                  return;
                }
                if (canSend) handleSendMessage();
                else if (!isInternalNote) startRecording();
              }}
              disabled={sendBlockedMissingPhone || (isInternalNote && !canSend)}
              aria-label={
                isRecording
                  ? t("input.recordVoiceAria")
                  : canSend || isInternalNote
                    ? t("input.sendAria")
                    : t("input.recordVoiceAria")
              }
              sx={{
                ...composerPrimaryActionButtonSx({
                  accent: isPersonalWhatsappThread ? "#128C7E" : undefined,
                  accentHover: isPersonalWhatsappThread ? "#0f6f64" : undefined,
                  size: COMPOSER_MOBILE_ACTION_BUTTON_SIZE_PX,
                }),
                ...(isPersonalWhatsappThread
                  ? { "&:hover": { backgroundColor: "#0f6f64" } }
                  : {}),
              }}
            >
              {canSend || isInternalNote ? (
                <Send fontSize="small" />
              ) : isRecording ? (
                <Stop fontSize="small" />
              ) : (
                <Mic fontSize="small" />
              )}
            </IconButton>
          </Box>
        </>
      ) : (
        integratedComposer
      )}
      {selectionToolbar}
      {isInternalNote && (
        <InternalNoteExpandedEditor
          open={isInternalEditorOpen}
          onClose={() => setIsInternalEditorOpen(false)}
          message={message}
          setMessage={setMessage}
          onSend={handleInternalEditorSend}
          openFilePicker={openFilePicker}
          file={file}
          files={files}
          onRemoveFile={onRemoveFile}
        />
      )}
    </Box>
  );
};

export default MessageInputField;
